import { Select } from "components/ui/select/Select"
import { isCategorylessTopLevel } from "../../furniture.constants"
import type { DefaultOptionType } from "antd/es/select"
import style from "./CategoriesSection.module.scss"

const { section, sectionTitle, field, labelRow, label, requiredMark } = style

type CategoryValue = string | number | null

type Props = {
	topLevelValue: CategoryValue
	topLevelOptions: DefaultOptionType[]
	onTopLevelChange: (value: CategoryValue) => void
	superValue: CategoryValue
	superOptions: DefaultOptionType[]
	onSuperChange: (value: CategoryValue) => void
	modelCategoryValue: CategoryValue
	modelCategoryOptions: DefaultOptionType[]
	onModelCategoryChange: (value: CategoryValue) => void
	topLevelDisabled?: boolean
	/**
	 * Per-level placeholder override. The batch panel (REQ-403) passes `Mixed` for exactly the levels
	 * the selection DISAGREES on, so such a level reads as Mixed instead of inviting a pick it hasn't
	 * made — and a level every object leaves equally unset keeps the ordinary "Select …" prompt,
	 * because agreeing on "nothing" is agreement, not a disagreement to announce. One flag for all
	 * three would make the lower levels lie every time they are legitimately empty (most visibly right
	 * after a top-level change, when they have no value yet by construction).
	 */
	mixedLabels?: { topLevel?: string; super?: string; object?: string }
	/**
	 * Keeps the Super + Object selects rendered even while their parent level is unset. The batch
	 * panel needs it: a selection can agree on the top level and disagree on the super category, and
	 * those lower levels must still show their own `Mixed` rather than disappearing. Off by default,
	 * so the single-object sidebar keeps its strict cascade (a level appears once its parent is set).
	 */
	showAllLevels?: boolean
}

export const CategoriesSection = ({
	topLevelValue,
	topLevelOptions,
	onTopLevelChange,
	superValue,
	superOptions,
	onSuperChange,
	modelCategoryValue,
	modelCategoryOptions,
	onModelCategoryChange,
	topLevelDisabled = false,
	mixedLabels,
	showAllLevels = false,
}: Props) => {
	// `Other` and `Custom` (#1346 / #1371) both name an object type that HAS no category, so neither
	// offers a super category or an object — the same rule for both, stated once in
	// `furniture.constants` rather than re-derived at each render site.
	const isCategoryless = isCategorylessTopLevel(topLevelValue)
	const showSuper = (showAllLevels || topLevelValue !== null) && !isCategoryless
	const showObject = (showAllLevels || (topLevelValue !== null && superValue !== null)) && !isCategoryless

	return (
		<div className={section}>
			<p className={sectionTitle}>Categories</p>
			<div className={field}>
				<div className={labelRow}>
					<span className={label}>Top level category</span>
					<span className={requiredMark}>*</span>
				</div>
				<Select
					variant="outlined"
					options={topLevelOptions}
					value={topLevelValue as string | number | null | undefined}
					onChange={(next) => onTopLevelChange(next as CategoryValue)}
					placeholder={mixedLabels?.topLevel ?? "Select category"}
					showSearch
					disabled={topLevelDisabled}
					optionFilterProp="label"
					renderInPlace
				/>
			</div>

			{showSuper ? (
				<div className={field}>
					<div className={labelRow}>
						<span className={label}>Super category</span>
						<span className={requiredMark}>*</span>
					</div>
					<Select
						variant="outlined"
						options={superOptions}
						value={superValue as string | number | null | undefined}
						onChange={(next) => onSuperChange(next as CategoryValue)}
						placeholder={mixedLabels?.super ?? "Select super category"}
						showSearch
						optionFilterProp="label"
						renderInPlace
					/>
				</div>
			) : null}

			{showObject ? (
				<div className={field}>
					<div className={labelRow}>
						<span className={label}>Object</span>
						<span className={requiredMark}>*</span>
					</div>
					<Select
						variant="outlined"
						options={modelCategoryOptions}
						value={modelCategoryValue as string | number | null | undefined}
						onChange={(next) => onModelCategoryChange(next as CategoryValue)}
						placeholder={mixedLabels?.object ?? "Select object"}
						showSearch
						optionFilterProp="label"
						renderInPlace
					/>
				</div>
			) : null}
		</div>
	)
}
